import { useState } from 'react'
import { useTranslation } from 'react-i18next'

/* Inline, stroke-based 24×24 icons — same weight/style as the rest of the app
   (fill:none, currentColor, round caps/joins). Kept local so the bottom nav is
   self-contained and matches the hand-rolled SVGs elsewhere in web/. */
const Icon = {
  dashboard: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
    </>
  ),
  truck: (
    <path d="M7 17a2 2 0 1 0 4 0 2 2 0 1 0-4 0M15 17a2 2 0 1 0 4 0 2 2 0 1 0-4 0M5 17H3V6a1 1 0 0 1 1-1h9v12M13 9h4l3 3v5h-2" />
  ),
  pin: (
    <>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </>
  ),
  bell: (
    <>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </>
  ),
  grid: (
    <>
      <circle cx="5" cy="5" r="1" />
      <circle cx="12" cy="5" r="1" />
      <circle cx="19" cy="5" r="1" />
      <circle cx="5" cy="12" r="1" />
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
      <circle cx="5" cy="19" r="1" />
      <circle cx="12" cy="19" r="1" />
      <circle cx="19" cy="19" r="1" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </>
  ),
  chart: (
    <>
      <path d="M3 3v18h18" />
      <rect x="7" y="11" width="3" height="6" />
      <rect x="12" y="7" width="3" height="10" />
      <rect x="17" y="13" width="3" height="4" />
    </>
  ),
  gear: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </>
  ),
}

function Glyph({ name }) {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {Icon[name]}
    </svg>
  )
}

export default function BottomNav({ activeTab, onTabChange, alertCount = 0 }) {
  const { t } = useTranslation()
  const [sheetOpen, setSheetOpen] = useState(false)

  // The items pinned to the bottom bar. `tab` maps to Dashboard's tab state.
  // Alerts routes to the real alert-workflow tab and carries the open-alert
  // badge; the live map now lives on the dashboard itself.
  const PRIMARY = [
    { tab: 'dashboard', label: t('nav.dashboard').slice(0, 4), icon: 'dashboard' },
    { tab: 'fleet',     label: t('nav.fleet'),                 icon: 'truck' },
    { tab: 'alerts',    label: t('nav.alerts'),                icon: 'bell', badge: true },
    { tab: 'health',    label: t('nav.health'),                icon: 'pin' },
  ]

  // Items that live behind "More".
  const more = [
    { tab: 'geofences', label: t('nav.geofences'), icon: 'pin' },
    { tab: 'history',   label: t('nav.history'),   icon: 'clock' },
    { tab: 'reports',   label: t('nav.reports'),   icon: 'chart' },
    { tab: 'settings',  label: t('nav.settings'),  icon: 'gear' },
  ]

  // "More" reads as active whenever the visible tab lives inside the sheet.
  const moreActive = more.some(m => m.tab === activeTab)

  function go(tab) {
    onTabChange(tab)
    setSheetOpen(false)
  }

  return (
    <>
      {/* Bottom sheet — rendered always so the slide transition runs both ways */}
      <div
        className={`bnav-backdrop${sheetOpen ? ' is-open' : ''}`}
        onClick={() => setSheetOpen(false)}
        aria-hidden={!sheetOpen}
      />
      <div
        className={`bnav-sheet${sheetOpen ? ' is-open' : ''}`}
        role="dialog"
        aria-label="More navigation"
        aria-hidden={!sheetOpen}
      >
        <div className="bnav-sheet__handle" />
        <div className="bnav-sheet__grid">
          {more.map(m => (
            <button
              key={m.tab}
              className={`bnav-sheet__item${activeTab === m.tab ? ' is-active' : ''}`}
              onClick={() => go(m.tab)}
            >
              <Glyph name={m.icon} />
              <span>{m.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Fixed bottom bar */}
      <nav className="bnav" aria-label="Primary">
        {PRIMARY.map(it => (
          <button
            key={it.tab}
            className={`bnav__item${activeTab === it.tab ? ' is-active' : ''}`}
            onClick={() => go(it.tab)}
            aria-current={activeTab === it.tab ? 'page' : undefined}
          >
            <span className="bnav__ico">
              <Glyph name={it.icon} />
              {it.badge && alertCount > 0 && (
                <span className="bnav__badge">{alertCount > 9 ? '9+' : alertCount}</span>
              )}
            </span>
            <span className="bnav__label">{it.label}</span>
          </button>
        ))}
        <button
          className={`bnav__item${moreActive || sheetOpen ? ' is-active' : ''}`}
          onClick={() => setSheetOpen(o => !o)}
          aria-expanded={sheetOpen}
          aria-haspopup="dialog"
        >
          <span className="bnav__ico"><Glyph name="grid" /></span>
          <span className="bnav__label">{t('nav.more')}</span>
        </button>
      </nav>
    </>
  )
}
